(ns app.front.view.admin.signal-debugger
  (:require [app.domain.workspace.model :as workspace-model]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.group.admin.states :as admin.states]
            [cloudpermit-commons.schemas.general :as general]
            [core.front.carbon :as cc]
            [core.front.comm :as comm]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [re-frame.std-interceptors :as interceptors]
            [schema-tools.core :as st]
            [schema.core :as s]))


;;;; # View Model


(def ^:private phase-names (map name workspace-model/phase-entity-types))


(s/defschema ^:private PathParams
  {:workspace-id workspace-model/WorkspaceId
   :phase        (apply s/enum phase-names)
   :signal       general/NonBlankString
   :branding     s/Str})


(def ^:private path-param-keys (keys PathParams))


(s/defschema ^:private QueryParams
  {:user-email general/NonBlankString})


(def ^:private query-param-keys (keys QueryParams))


(s/defschema ^:private Params (st/merge PathParams QueryParams))


(s/defschema ^:private Query (p/map-vals s/maybe Params))


(def ^:private check-complete-query (s/checker Params))


(def ^:private query-keys (keys Query))


(def ^:private default-query
  (into {}
        (map (juxt identity (constantly nil)))
        query-keys))


(s/defn ^:private params->query :- Query [params :- Params]
  (into {}
        (map (juxt identity #(get params %)))
        query-keys))


(s/defn ^:private get-route-query :- (s/maybe Query) [db]
  (let [parameters (-> db :route/match :parameters)]
    (p/if-all-let [path-params  (not-empty (:path parameters))
                   query-params (not-empty (:query parameters))]
      (merge path-params query-params)
      nil)))


(defn- entity-type-name->entity-type [type-name] (keyword "entity.type" type-name))


(defn- signal-name->signal [signal-name] [(keyword "process.signal" signal-name)])


(defn- query->command-params [{:keys [workspace-id phase signal user-email]}]
  {:workspace-id   workspace-id
   :phase-type     (entity-type-name->entity-type phase)
   :process/signal (signal-name->signal signal)
   :user-email     user-email})


;;;; # Subs


(rf/reg-sub ::route-query :-> get-route-query)


(rf/reg-sub ::state :-> ::state)


(rf/reg-sub ::query
  :<- [::state]
  :-> :query)


(rf/reg-sub ::phase-entity-type
  :<- [::query]
  :-> #(some-> (:phase %) entity-type-name->entity-type))


(rf/reg-sub ::signal
  :<- [::query]
  :-> #(some-> (:signal %) signal-name->signal))


(rf/reg-sub ::query-validation-errors
  :<- [::query]
  :-> check-complete-query)


(rf/reg-sub ::view-state
  :<- [::route-query]
  :<- [::state]
  :-> (fn [[route-query {loading :loading?, error :error?, :keys [query]}]]
        (cond
          loading :loading
          error :error
          (not= query route-query) :modified
          :else :done)))


(rf/reg-sub ::rejecting-guards
  :<- [::state]
  :-> :rejecting-guards)

(rf/reg-sub ::signals
  :<- [::state]
  :-> :signals)


;;;; # Events


(def ^:private state-path (interceptors/path ::state))


(rf/reg-event-db ::query-assoc
  [state-path]
  (fn [state [_ k v]] (update state :query assoc k v)))


(rf/reg-event-fx ::submit-query
  (fn [{db :db} _]
    (let [route-query (get-route-query db)
          query       (-> db ::state :query)]
      (if (not= query route-query)
        {:route/push {:route  :view/admin.signal-debugger-with-params
                      :params (select-keys query path-param-keys)
                      :query  (select-keys query query-param-keys)}}
        ;; HACK: If the route does not change, the Reitit controller does not fire so have to dispatch `::start` here:
        {:dispatch [::start query]}))))


(rf/reg-event-fx ::check-guards
  [state-path]
  (fn [{state :db} [_ query]]
    {:db           (assoc state :loading? true)
     :comm/command {:command          :admin/rejecting-guards
                    :data             (query->command-params query)
                    :on-success       [::check-guards.success]
                    :on-done          [::check-guards.error]
                    :finally-dispatch [::check-guards.finally]}}))


(rf/reg-event-db ::check-guards.success
  [state-path]
  (fn [state [_ _ {:keys [rejecting-guards signals]} _]]
    (assoc state
           :error? false
           :rejecting-guards rejecting-guards
           :signals signals)))


(rf/reg-event-fx ::check-guards.error
  [state-path]
  (fn [{state :db} [_ status body request-id]]
    {:db       (assoc state :error? true)
     :dispatch (comm/error-notification-evt status body request-id)}))


(rf/reg-event-db ::check-guards.finally
  [state-path]
  (fn [state _] (assoc state :loading? false)))


(rf/reg-event-fx ::start
  (fn [{:keys [db]} [_ params]]
    (if params
      (let [query (params->query params)]
        {:db       (assoc-in db [::state :query] query)
         :dispatch [::check-guards query]})
      {:db (assoc-in db
                     [::state :query]
                     (merge default-query
                            ;; This should take care of :branding path param "/gov"
                            (-> db :route/match :parameters :path)))})))


(rf/reg-event-db ::stop
  (fn [db _] (dissoc db ::state)))


;;;; # Views


(def ^:private query-form
  (let [query-setter    (fn [k] (fn [ev] (rf/dispatch-sync [::query-assoc k (-> ev .-target .-value)])))
        set-ws-id!      (query-setter :workspace-id)
        set-phase!      (fn [v] (>evt [::query-assoc :phase v]))
        set-signal!     (query-setter :signal)
        set-user-email! (query-setter :user-email)
        submit!         (fn [_] (>evt [::submit-query]))]
    #_{:clj-kondo/ignore [:def-fn]}
    (fn [{:keys [workspace-id phase signal user-email]}]
      (let [tr                (<sub [:tr])
            validation-errors (<sub [::query-validation-errors])]
        [:form
         [:div.bx--row.form-page__row
          [ui/text-input {:class       "bx--col"
                          :value       workspace-id
                          :on-change   set-ws-id!
                          :label-text  (tr [:workspace.id])
                          :helper-text workspace-model/workspace-id-pattern
                          :required    true
                          :invalid     (contains? validation-errors :workspace-id)}]]
         [:div.bx--row.form-page__row
          [ui-select/select {:class       "bx--col full-width-select"
                             :id          "phase"
                             :values      phase-names
                             :value       phase
                             :on-change   set-phase!
                             :label-text  (tr [:workspace/phase])
                             :helper-text (pr-str (<sub [::phase-entity-type]))
                             :required    true
                             :invalid     (contains? validation-errors :phase)}]]
         [:div.bx--row.form-page__row
          [ui/text-input {:class       "bx--col"
                          :value       signal
                          :on-change   set-signal!
                          :label-text  (tr [:signal-debugger/signal-name])
                          :helper-text (pr-str (<sub [::signal]))
                          :required    true
                          :invalid     (contains? validation-errors :signal)}]]
         [:div.bx--row.form-page__row
          [ui/text-input {:class       "bx--col"
                          :value       user-email
                          :on-change   set-user-email!
                          :label-text  (tr [:user.email])
                          :helper-text (tr [:signal-debugger/user-email-help])
                          :required    true
                          :invalid     (contains? validation-errors :user-email)}]]
         [:div.bx--row.form-page__row>div.bx--col
          (cc/button {:kind     :primary
                      :disabled (boolean validation-errors)
                      :on-click submit!}
                     (tr [:signal-debugger/submit]))]]))))


(defn- render-guard [quiet-tr guard-name]
  [:li
   [:code (pr-str guard-name)]
   (when-some [explanation (quiet-tr [guard-name])]
     (str " (" explanation ")"))])


(defn- render-guards [rejecting-guards]
  (let [tr         (<sub [:tr])
        quiet-tr   (<sub [:tr/quiet])
        rejections (seq rejecting-guards)]
    [:div.bx--row
     (cc/inline-notification
       {:class-name        "bx--col max-w-none"
        :hide-close-button true
        :kind              (if rejections :error :success)
        :title             (if rejections (tr [:signal-debugger/rejected]) (tr [:signal-debugger/applicable]))
        :subtitle          (if rejections "" (tr [:signal-debugger/applicable-explanation]))}
       (when rejections
         [:ul.list-disc
          (->> rejections
               (map (fn [guard-name] ^{:key guard-name} [render-guard quiet-tr guard-name])))]))]))


(defn- view [_]
  (let [tr    (<sub [:tr])
        query (<sub [::query])]
    [:div.cloudpermit-page.admin-page
     [:div.bx--row.page-heading
      [:h1.bx--col-xs-12 (tr [:admin-tools/signal-debugger])]]

     [:section
      [query-form query]

      (case (<sub [::view-state])
        (:modified :error) nil
        :loading (cc/skeleton-text {:class-name "bx--row"
                                    :paragraph  true})
        :done [render-guards (<sub [::rejecting-guards])])

      (let [transitions   (<sub [::signals])
            common-guards (admin.states/find-common-guards transitions)]
        [:ul
         [:li.border-2.border-ui-violet.border-solid.mb-4.rounded-lg.bg-ui-violet-20
          [:div.mb-2.pl-4
           [:h4 "Common guards"]
           [:ul
            (futil/for-frag [guard (sort-by (comp name first) common-guards)]
              [:li [admin.states/guard-visualization guard]])]]
          [:ul
           (futil/for-frag [transition transitions]
             [admin.states/transition-visualization (util/mmap transition common-guards)])]]])]]))


;;;; # Routes


(def route
  {:name        :view/admin.signal-debugger
   :view        view
   :controllers [{:start (fn [_] (>evt [::start nil]))
                  :stop  (fn [_] (>evt [::stop]))}]})


(def params-route
  {:name        :view/admin.signal-debugger-with-params
   :parameters  {:path  PathParams
                 :query QueryParams}
   :view        view
   :controllers [{:parameters {:path  (keys PathParams)
                               :query (keys QueryParams)}
                  :start      (fn [{:keys [path query]}] (>evt [::start (merge path query)]))
                  :stop       (fn [_] (>evt [::stop]))}]})
